<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import EditIndividualBusinessModal from '../components/EditIndividualBusinessModal.vue';
import { useEditIndividualBusinessStore } from '../pages/useEditIndividualBusinessStore';
import NewBaseTableContent from '@/corePagesModule/BaseTableContent/NewBaseTableContent.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { i18n } from '@/i18n';
import { editIndividualBusinessChildContentHeader } from '../constants';
import { isLoading } from '@/coreDomainsModule/uiLoadingState';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import { computed, onMounted } from 'vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';

const t = i18n.t;

const store = useEditIndividualBusinessStore();
const { state } = storeToRefs(store);

onMounted(async () => {
  state.value.isBaseBusinessView = false;
  await store.initialization();
});

const getChildBusinessModalDescription = computed(() => {
  const allAvailableNumber = state.value.tableInfo.childBusiness.pagingState.totalResultsAvailable ?? 0;
  const text1 = allAvailableNumber > 50 ? t('該当する取引の先頭50件のみ表示しています。') : '';
  const text2 = t('ここから出荷している取引', { amount: allAvailableNumber ? allAvailableNumber.toString() : '全' });
  return [text1, text2];
});
</script>
<template>
  <div class="edit-individual-business">
    <div class="edit-individual-business__header__filter-pagination">
      <FilterModalButton
        filter-modal-type="editIndividualBusiness"
        :select-input-index="50"
        :filter-items="store.getFilterItemsValue().value"
        @apply="(filteredContent) => store.onAppliedFilterModal(filteredContent)"
      />
      <Pagination
        :paging-options="state.tableInfo.pagingState"
        :is-loading="isLoading(state.tableInfo.loadingState.status)"
        @on-change-page="store.onChangePaging($event)"
      />
    </div>
    <NewBaseTableContent
      :key="state.renderingKey"
      :prop-table-content-properties="state.tableInfo.tableContentProperties"
      :prop-display-objects="state.tableInfo.tableContentItems"
      :prop-loading-status="state.tableInfo.loadingState"
      :prop-table-type="'editIndividualBusiness'"
      @on-click-sort="(sortKey, isAsc) => store.sortTableContentItems(sortKey, isAsc)"
    />
    <EditIndividualBusinessModal />
    <InfoModal
      :modal-state="state.modalState.isOpening.childBusinessList"
      :modal-width="1300"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('適用')"
      @on-back="
        () => {
          state.modalState.isOpening.childBusinessList = false;
          if (!state.modalState.isOpening.loading) state.modalState.isOpening.edit = true;
        }
      "
      @on-next="
        () => {
          state.modalState.isOpening.loading = true;
          state.modalState.isOpening.childBusinessList = false;
          store.putEditedAggregateBusiness();
        }
      "
    >
      <template #title>{{ t('編集') }}</template>
      <template #description>
        <template v-if="state.tableInfo.childBusiness.loadingState.status.type === 'success'">
          {{ getChildBusinessModalDescription[0] }} <br v-if="getChildBusinessModalDescription[0]" />{{
            getChildBusinessModalDescription[1]
          }}
        </template>
      </template>
      <template #default>
        <NewBaseTableContent
          :prop-table-content-properties="editIndividualBusinessChildContentHeader"
          :prop-display-objects="state.tableInfo.childBusiness.tableContentItems"
          :prop-loading-status="state.tableInfo.childBusiness.loadingState"
          prop-table-type="editIndividualBusinessChildBusiness"
          class="edit-individual-business__child-business-modal__table"
        />
      </template>
    </InfoModal>
    <InfoModal :modal-state="state.modalState.isOpening.loading" icon="loading" :enable-outside-click="false">
      <template #title> {{ t('編集中です・・・') }} </template>
    </InfoModal>
    <InfoModal
      :modal-state="state.modalState.isOpening.commonError"
      icon="error"
      color-style="error"
      :back-button-label="t('確認')"
      :enable-outside-click="false"
      is-disabled-next-button
      @on-back="
        () => {
          state.modalState.isOpening.commonError = false;
          state.modalState.isOpening.edit = true;
        }
      "
    >
      <template #title>{{ t('エラーが発生しました') }}</template>
      <template #description> {{ t('サーバーエラーが発生しました。') }} <br />{{ t('時間をおいて再度お試しください。') }} </template>
    </InfoModal>
    <InfoModal
      :modal-state="state.modalState.isOpening.duplicationError"
      icon="error"
      color-style="error"
      :back-button-label="t('確認')"
      :enable-outside-click="false"
      is-disabled-next-button
      @on-back="
        () => {
          state.modalState.isOpening.duplicationError = false;
          state.modalState.isOpening.edit = true;
        }
      "
    >
      <template #title>{{ t('既存の取引が存在します') }}</template>
      <template #description>{{ t('編集後の取引は既に存在するため、編集ができませんでした。') }} </template>
    </InfoModal>
    <InfoModal
      :modal-state="state.modalState.isOpening.success"
      :next-button-label="t('確認')"
      :enable-outside-click="false"
      @on-next="async () => await store.initialization()"
    >
      <template #title>{{ t('編集完了しました') }}</template>
    </InfoModal>
  </div>
</template>
<style lang="scss" scoped>
.edit-individual-business {
  display: grid;
  gap: 8px;

  &__header {
    &__filter-pagination {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: space-between;
    }
  }

  &__child-business-modal {
    &__table {
      max-height: 396px !important;
      min-height: 0;
      color: $textSecondary;
    }
  }
}
</style>
